import {
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonLabel,
  IonGrid,
  IonRange,
} from "@ionic/react";
import { Canvas } from "@react-three/fiber";
import { Suspense, useEffect, useRef, useState } from "react";
import ThreeDModel, { ModelsHolder } from "./3DModel";
import { MeasurementResource, ObjectModel } from "../common/types";

import { KeyLabel } from "../common/domainConfig";
interface MeasurmentResultProps {
  objectModels: ObjectModel[];
  activeModel?: number;
  visibleMeasurements: KeyLabel[];
}
const MeasurmentResult = (props: MeasurmentResultProps) => {
  const { objectModels, activeModel } = props;

  const [activeModelIndex, setActiveModelIndex] = useState<number>(
    activeModel ?? objectModels.length - 1
  );
  const [showMeasurement, setShowMeasurement] = useState<string>("");
  useEffect(() => {
    if (activeModelIndex < 0 || activeModelIndex < objectModels.length) {
      setMeasurements(objectModels[activeModelIndex].measurements);
    }
  }, [activeModelIndex]);

  const [measurements, setMeasurements] = useState<MeasurementResource>(
    objectModels[activeModelIndex].measurements
  );
  if (objectModels.length === 0) {
    return <div>No 3D model available</div>;
  }

  const buttonsBellow = true;
  function showHideVisualEffect(name: string) {
    setShowMeasurement(name);
  }
  const models: ModelsHolder = {
    models: props.objectModels,
    activeModel: props.activeModel ?? objectModels.length - 1,
  };
  const setActiveModel = (index: number) => {
    setActiveModelIndex(index);
  };

  const buttonGrid = () => {
    const perRow = 2;
    let rows = Math.ceil(props.visibleMeasurements.length / perRow);
    const colSpan = 12 / perRow + "";
    let grid = [];
    for (let i = 0; i < rows; i++) {
      let row = [];
      for (let j = 0; j < perRow; j++) {
        let index = i * perRow + j;
        if (index < props.visibleMeasurements.length) {
          const measurement = props.visibleMeasurements[index];
          const name = measurement.label;
          const text = `${name} ${getMeasurementByName(measurement.key)}`;
          const className =
            "text-lg " +
            (showMeasurement === measurement.key
              ? "selectedMeasurement measurementBtn"
              : "measurementBtn");
          row.push(
            <IonCol
              key={index}
              size={colSpan}
              className={className}
              onClick={() =>
                showHideVisualEffect(props.visibleMeasurements[index].key)
              }
            >
              <IonItem>{text}</IonItem>
            </IonCol>
          );
        }
      }

      grid.push(<IonRow key={i}>{row}</IonRow>);
    }
    return <IonGrid className="overlay">{grid}</IonGrid>;
  };
  const timeAgoString = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const diffInSeconds = diff / 1000;
    const diffInMinutes = diffInSeconds / 60;
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;
    const diffInMonths = diffInDays / 30;
    const diffInYears = diffInMonths / 12;
    if (diffInYears >= 1) {
      return Math.round(diffInYears) + " years ago";
    } else if (diffInMonths >= 1) {
      return Math.round(diffInMonths) + " months ago";
    } else if (diffInDays >= 1) {
      return Math.round(diffInDays) + " days ago";
    } else {
      return "Today";
    }
  };
  const getMeasurementByName = (name: string, meas?: MeasurementResource) => {
    let res = 0;
    if (!meas) {
      meas = measurements;
    }

    res = getValue(meas);
    console.log(`Measurement for ${name}: ${res}`);
    let unit = "cm";
    if (name === "body_fat") {
      unit = "%";
    }else if (name === "weight") {
        unit = "kg";
    }
    return res > 0 ? res + " " + unit : "x";

    function getValue(meas: MeasurementResource) {
      switch (name) {
        case "height":
          return Math.round(meas.height);
        case "head":
          return Math.round(meas.head);
        case "neck":
          return Math.round(meas.neck);
        case "chest":
          return Math.round(meas.chest);
        case "arm_outer":
          return Math.round(meas.arm_outer);
        case "waist":
          return Math.round(meas.waist);
        case "buttocks":
          return Math.round(meas.buttocks);
        case "leg_inner":
          return Math.round(meas.leg_inner);
        case "leg_circ":
          return Math.round(meas.leg_circ || 0);
        case "arm_circ":
          return Math.round(meas.arm_circ || 0);
        case "waist_female":
          return Math.round(meas.waist_female || 0);
        case "body_fat":
            return Math.round(meas.body_fat || 0);

        default:
          return 0;
      }
    }
  };

  return (
    <IonRow>
      {objectModels.length > 1 && props.activeModel === undefined ? (
        <IonCol size="12" className="px-8 ">
          <IonLabel className="py-2 text-center text-md">
            {timeAgoString(new Date(objectModels[activeModelIndex].createdAt))}
          </IonLabel>
          <></>
          <IonRange
            aria-label="Range with pin"
            snaps={true}
            value={activeModelIndex}
            min={0}
            //debounce={200}
            max={objectModels.length - 1}
            onIonInput={({ detail }) => setActiveModel(detail.value as number)}
            onIonChange={({ detail }) => {
              setActiveModel(detail.value as number);
            }}
          ></IonRange>
        </IonCol>
      ) : (
        <></>
      )}
      <IonCol size="12">
        <div className="resultPhoto m-0">
          <Canvas
            camera={{
              fov: 40,
              aspect: window.innerWidth / window.innerHeight,
              zoom: 1.3,
              near: 1,
              far: 1100,
            }}
          >
            <color attach="background" args={["white"]} />
            <Suspense fallback={null}>
              <ThreeDModel
                models={models}
                activeModel={activeModelIndex}
                showMeasurement={showMeasurement}
              />
            </Suspense>
          </Canvas>
        </div>
      </IonCol>
      {buttonsBellow && buttonGrid()}
      {!buttonsBellow && (
        <div className="overlay">
          <IonCol>
            <IonList className="measureList overflow-y-scroll">
              {props.visibleMeasurements.map((measurement, index) => {
                const name = measurement.label;
                const text = `${name} ${getMeasurementByName(measurement.key)}`;
                return (
                  <IonItem
                    key={index}
                    color="0xff38a9"
                    className={
                      showMeasurement === measurement.key
                        ? "selectedMeasurement measurementBtn"
                        : "measurementBtn left-0"
                    }
                    onClick={() => {
                      showHideVisualEffect(measurement.key);
                    }}
                  >
                    <div className="text-nowrap text-xs p-0">{text}</div>
                  </IonItem>
                );
              })}
            </IonList>
          </IonCol>
        </div>
      )}
    </IonRow>
  );
};

export default MeasurmentResult;
